import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { Fragment, useEffect, useMemo, useRef } from 'react'
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch
} from 'react-hook-form'

import Icon from 'components/Icon'
import { GrafanaMatchPreview } from 'components/status-checks/form/status-check/grafana/GrafanaMatchPreview'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useGetGrafanaRuleInstances } from 'queries/grafana/use-get-grafana-rule-instances'
import {
  countMatchingInstances,
  GRAFANA_MATCHER_OPS,
  type GrafanaMatcher,
  getFilterCandidates
} from 'utils/status-checks/grafana-instance-filters'
import { findAlertIdInUrl } from 'utils/status-checks/status-check-integrations-util-grafana'

import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'

const fieldsToWatch = [
  'externalIntegration',
  'monitorUrl',
  'grafanaSelector.filters',
  'multiSelectTags'
] as const

function isCompleteMatcher({ label }: GrafanaMatcher) {
  return Boolean(label.trim())
}

export const GrafanaInstanceFilters: React.FC = () => {
  const { statusCheckId } = useStatusCheckDetailsContext()
  const { control, setValue } = useFormContext<StatusCheckFormValues>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'grafanaSelector.filters'
  })

  const [externalIntegration, monitorUrl, filters = [], multiSelectTags] =
    useWatch({
      control,
      name: fieldsToWatch
    })

  const ruleUid = findAlertIdInUrl(monitorUrl)
  const { data: instances, isError } = useGetGrafanaRuleInstances({
    integration: externalIntegration,
    multiSelectTags,
    ruleUid,
    statusCheckId
  })

  // Filters belong to one rule, so picking another rule starts over
  const previousRuleUid = useRef(ruleUid)
  useEffect(() => {
    if (!ruleUid) return
    if (previousRuleUid.current && previousRuleUid.current !== ruleUid) {
      setValue('grafanaSelector.filters', [], { shouldDirty: true })
    }
    previousRuleUid.current = ruleUid
  }, [ruleUid, setValue])

  const candidates = useMemo(
    () => getFilterCandidates(instances ?? []),
    [instances]
  )

  if (!ruleUid) return null

  const instanceCount = instances?.length ?? 0
  const completeFilters = filters.filter(isCompleteMatcher)

  return (
    <Stack spacing={2}>
      {/* e.g. response too large, or a Grafana version that doesn't list instances */}
      {(isError || instances === null) && (
        <Alert severity="info">
          We couldn&apos;t load this alert&apos;s current instances, so there
          are no label suggestions. You can still add filters by typing label
          names.
        </Alert>
      )}
      {instanceCount > 1 && fields.length === 0 && (
        <Alert severity="info">
          This alert has {instanceCount} instances. Without a filter, this
          Health Check is unhealthy when any of them fires. Add a filter to
          respond to specific instances only.
        </Alert>
      )}
      {fields.length > 0 && (
        <>
          <Box
            sx={{
              alignItems: 'flex-start',
              columnGap: 2,
              display: 'grid',
              gridTemplateColumns: '1fr 100px 1fr auto',
              rowGap: 2
            }}
          >
            {fields.map(({ id }, index) => {
              const candidate = candidates.find(
                ({ label }) => label === filters[index]?.label
              )

              return (
                <Fragment key={id}>
                  <Controller
                    control={control}
                    name={`grafanaSelector.filters.${index}.label`}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete<string, false, false, true>
                        forcePopupIcon
                        freeSolo
                        openOnFocus
                        inputValue={field.value}
                        options={candidates.map(({ label }) => label)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={Boolean(error)}
                            helperText={error?.message}
                            label="Label"
                          />
                        )}
                        onInputChange={(_, value) => field.onChange(value)}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`grafanaSelector.filters.${index}.op`}
                    render={({ field }) => (
                      <TextField {...field} select label="Operator">
                        {GRAFANA_MATCHER_OPS.map((op) => (
                          <MenuItem key={op} value={op}>
                            {op}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`grafanaSelector.filters.${index}.value`}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete<string, false, false, true>
                        forcePopupIcon
                        freeSolo
                        openOnFocus
                        inputValue={field.value}
                        options={candidate?.values ?? []}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={Boolean(error)}
                            helperText={
                              error?.message ??
                              (candidate &&
                                candidate.presentOn < instanceCount &&
                                `Present on ${candidate.presentOn} of ${instanceCount} instances`)
                            }
                            label="Value"
                          />
                        )}
                        onInputChange={(_, value) => field.onChange(value)}
                      />
                    )}
                  />
                  <IconButton
                    aria-label={`Remove filter ${index + 1}`}
                    color="error"
                    size="large"
                    onClick={() => remove(index)}
                  >
                    <Icon icon="circleTrash" />
                  </IconButton>
                </Fragment>
              )
            })}
          </Box>
          <Typography color="text.secondary" variant="body2">
            Filters are combined with AND. To match any of several values, use
            one filter with =~, e.g. pod =~ a|c
          </Typography>
        </>
      )}
      <Button
        startIcon={<Icon icon="plus" />}
        sx={{ alignSelf: 'flex-start' }}
        variant="outlined"
        onClick={() =>
          append({
            label: candidates.find(({ varies }) => varies)?.label ?? '',
            op: '=',
            value: ''
          })
        }
      >
        Add filter
      </Button>
      {instances && completeFilters.length > 0 && (
        <GrafanaMatchPreview
          instanceCount={instanceCount}
          matchCount={countMatchingInstances(instances, completeFilters)}
        />
      )}
      <Controller
        control={control}
        name="grafanaSelector.haltWhenSilenced"
        render={({ field: { value, onChange, ...field } }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={value}
                onChange={(_, checked) => onChange(checked)}
              />
            }
            label="Still halt when this alert is silenced"
          />
        )}
      />
    </Stack>
  )
}
